import { REPORT } from '@consts/reports';
import { useReportLoad } from '@hooks/useReportLoad';
import { BaseReport } from '../BaseReport';
import { average, countUnique, sum } from '@utils/summaries';
import { getUniqueDates, groupByKeys } from '@utils/data';
import { basicSort, dateSort } from '@utils/sort';
import { useRange } from '@hooks/useRange';
import { chart } from '@utils/charts';
import { styleDate, styleDateRange } from '@utils/styles';
import { MaterialMap } from '@consts/materials';

import type { Materials } from '@consts/materials';

interface MaterialTypeByResellerProps {
    dateRange: { startDate: string; endDate: string };
    material: Materials;
}

export const MaterialTypeByReseller = ({ dateRange, material }: MaterialTypeByResellerProps) => {
    const materialId = MaterialMap[material];

    const [range, setRange] = useRange('MaterialTypeByReseller');

    const { data, hasData, loading } = useReportLoad({
        reportType: REPORT.RESELLER_MATERIAL_TYPE_PERFORMANCE_AS_A_PERCENTAGE_BY_RESELLER,
        params: { ...dateRange, range, materialId },
    });

    const resellers = groupByKeys(data, ['resellerName', 'date']);

    const dateKeys = getUniqueDates(data, range);

    return (
        <BaseReport
            title={`Material Type % Rate by Reseller (${material} Film)`}
            description={`This graph shows you the % of ${material} cuts (out of their total cuts across all materials) each of your Resellers did by month, allowing you to compare them to each other.`}
            rangeText={styleDateRange([dateRange.startDate, dateRange.endDate])}
            loading={loading}
            hasData={hasData}
            range={range}
            setRange={setRange}
            tableOptions={{
                headers: ['Date', 'Reseller Name', 'Material %', 'Total Cuts'],
                data: data.map((item) => [item.date, item.resellerName, item.materialPct, item.totalCuts]),
                summaries: [countUnique, countUnique, average, sum],
                style: [styleDate(range)],
                sort: [dateSort(range), basicSort, basicSort, basicSort],
            }}
            chartOptions={chart(
                500,
                [{ name: 'Material %', opposite: false }],
                dateKeys,
                [
                    ...Object.keys(resellers)
                        .sort()
                        .map((reseller: string, index: number) => {
                            const data = dateKeys.map(({ date }) => resellers[reseller]?.[date]);

                            return {
                                visible: index < 5,
                                name: reseller,
                                data: data.map((item) => ({ r: item?.totalCuts || 0, y: item?.materialPct || 0 })),
                                tooltip: {
                                    pointFormat: '{point.name}Material %: {point.y}<br/> Total Cuts: {point.r}',
                                },
                            };
                        }),
                ],
                { min: 0, max: 100 }
            )}
        />
    );
};
